
import { Component, EventEmitter, Input, OnDestroy, OnInit, Optional, Output, ChangeDetectionStrategy } from "@angular/core";
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { isEmpty } from "lodash";
import { IEntitySearchDoc } from "rl-common/models/i-entity-search-doc";
import { IQueryNode } from "rl-common/models/i-query-node";
import { IRecordTitle } from "rl-common/models/i-record-title";
import { CharDataType, CharTypeId, ConstUtils } from "rl-common/rl-common.consts";
import { AssociationService } from "rl-common/services/associations/association.service";
import { ParentEntityService } from "rl-common/services/entity/parent-entity/parent-entity.service";
import { ModDetailService } from "rl-common/services/mod-detail/mod-detail.service";
import { QueryUtil } from "rl-common/utils";
import { Subscription } from "rxjs";
import { SearchFieldNames } from "../../../../entities/entity-search/query.models";
import { InputListResultsComponent } from "./input-list-results/input-list-results.component";
import { IValidateInputListResult } from "./input-list.models";
import { OneConfigService } from "../../../../../services/one-config/one-config.service";

export enum ValidateMode {
	RecordID,
	Title,
	Characteristic
}

export interface IValidateEvent {
	validRecordsDeprecated: IRecordTitle[];
	validRecords: IEntitySearchDoc[];
	deselectedRecords?: IEntitySearchDoc[];
}

export interface InputType {
	text: string,
	value: number,
	characteristicId: number
}

@Component({
	selector: "rl-input-list",
	templateUrl: "./input-list.component.html",
	styleUrls: ["./input-list.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [ReactiveFormsModule, InputListResultsComponent]
})
export class InputListComponent implements OnInit, OnDestroy {

	@Input()
	childCharTypeId: number;

	@Input()
	charTypeId: number;

	@Input()
	matchesParentQuery: IQueryNode;

	@Input()
	includeParentRecordId = false;

	@Input()
	isAssociatingChild = true;

	@Input()
	parentCharTypeId: CharTypeId;

	@Input()
	parentRecordId: number;

	@Input()
	templateId: number;

	@Output()
	onValidate = new EventEmitter<IValidateEvent>();

	results: IValidateInputListResult;
	previousResults: IEntitySearchDoc[];
	toBeRemoved: IEntitySearchDoc[] = [];

	inputTypeList: InputType[] = [
		{ text: "Rightsline ID", value: ValidateMode.RecordID, characteristicId: -1 },
		{ text: "Exact Record Name", value: ValidateMode.Title, characteristicId: -1 }
	];

	inputListForm: UntypedFormGroup;
	private readonly _subs: Subscription[] = [];

	constructor(
		private readonly _associationService: AssociationService,
		private readonly _parentEntityService: ParentEntityService,
		@Optional() private readonly _modDetailService: ModDetailService,
		private readonly _oneConfigService: OneConfigService,
		private fb: UntypedFormBuilder
	) { }

	get rawInput() {
		return this.inputListForm.get("rawInputControl").value;
	}

	get validateMode() {
		return this.inputListForm.get("inputTypeControl").value;
	}

	selectedInputType: InputType;

	ngOnInit() {
		this.inputListForm = this.fb.group({
			inputTypeControl: this.inputTypeList[0],
			rawInputControl: [""]
		});

		this.selectedInputType = this.inputTypeList[0];

		const characteristics = this._oneConfigService.getCharTypeChars(this.childCharTypeId);
		const identifiers = characteristics.filter(x => x.dataTypeID == CharDataType.Identifier);

		if (identifiers && identifiers.length > 0) {
			identifiers.forEach((x) => {
				this.inputTypeList.push({ text: x.label, value: ValidateMode.Characteristic, characteristicId: x.characteristicID });
			});
		}
	}

	private splitRows() {
		return this.rawInput.split(/\r\n|\r|\n/g).filter(x => !isEmpty(x));
	}

	validate() {
		const recordIds = this.selectedInputType.value === ValidateMode.RecordID ? this.splitRows().filter(Number).map(i => +i) : [];
		const titles = this.selectedInputType.value === ValidateMode.Title ? this.splitRows() : [];
		const identifiers = this.selectedInputType.value === ValidateMode.Characteristic ? this.splitRows() : [];
		const identifierCharacteristicId = identifiers ? this.selectedInputType.characteristicId : null;
		const parentParams = this.parentParams();
		const parentCharTypeId = parentParams[0];
		const parentTemplateId = parentParams[1];
		const parentRecordId = parentParams[2];
		let query = {};
		if (recordIds.length > 0) {
			query = QueryUtil.$or(QueryUtil.$eq_any(SearchFieldNames.Entity.recordID, recordIds))
		}

		if (this.charTypeId === CharTypeId.Right && this.childCharTypeId === CharTypeId.Catalog) {
			query = QueryUtil.$and(this.matchesParentQuery, query);
		}

		const sub = this._associationService.validateInputList(parentCharTypeId, parentTemplateId, this.childCharTypeId, recordIds, titles, identifiers, identifierCharacteristicId,
			parentRecordId, !this.isAssociatingChild, query)
			.subscribe(response => {
				let results = this.excludeNonApplicableTemplates(response);
				results = this.excludeInactiveCatalogs(results); // RL-7759/RL-3988
				this.results = results;
				const validRecordsDeprecated: IRecordTitle[] = results.valid.map(r => {
					return { recordId: r.recordID, title: r.title };
				});
				if (!this.previousResults) {
					this.previousResults = results.valid;
				} else {
					const prevId = this.previousResults.map(x => x.recordID);
					const currentId = results.valid.map(x => x.recordID);
					const remove = prevId.filter(x => currentId.indexOf(x) < 0);
					this.toBeRemoved = this.previousResults.filter(x => remove.indexOf(x.recordID) >= 0);
					this.previousResults = results.valid;
				}
				this.onValidate.emit({
					validRecordsDeprecated: validRecordsDeprecated,
					validRecords: results.valid,
					deselectedRecords: this.toBeRemoved
				});
			});

		this._subs.push(sub);
	}

	/**
	 * Builds a tuple that contains all the parent parameters for the validation sproc
	 * @returns [parentCharTypeId, parentTemplateId, parentRecordId]
	 */
	private parentParams(): [number, number, number] {
		let tuple: [number, number, number];

		// pull the parent params from the explicitly passed parent ids, parent entity service, or the mod detail service
		if (ConstUtils.isComponentLevelCharType(this.charTypeId) && this._parentEntityService) {
			tuple = [this.parentCharTypeId ?? this._parentEntityService?.charTypeId ?? 0, this._parentEntityService?.templateId ?? 0, this.parentRecordId ?? this._parentEntityService?.recordId ?? null];
		} else {
			tuple = [this._modDetailService?.charTypeId ?? 0, this._modDetailService?.entity?.templateId ?? 0, this._modDetailService?.recordId ?? null];
		}

		// set the parent record id to null if we're not meant to send it or it doesn't exist
		const parentExists = tuple[2] > 0;
		if (!this.includeParentRecordId || !parentExists) {
			tuple[2] = null;
		}

		return tuple;
	}

	private applicableChildTemplateIds(): number[] {
		if (!this.isAssociatingChild || !this.charTypeId) {
			return [];
		}
		const templateId = this.templateId ?? (ConstUtils.isComponentLevelCharType(this.charTypeId) && this._parentEntityService
			? this._parentEntityService.templateId
			: this._modDetailService?.entity?.templateId);
		if (!templateId) {
			return [];
		}
		return this._oneConfigService.getChildAssocTemplateIds(this.charTypeId, templateId, this.childCharTypeId);
	}

	private excludeNonApplicableTemplates(results: IValidateInputListResult): IValidateInputListResult {
		const applicableTemplateIds = this.applicableChildTemplateIds();
		if (applicableTemplateIds.length === 0) {
			return results;
		}
		const nonApplicable = results.valid.filter(x => !applicableTemplateIds.includes(x.templateID));
		if (nonApplicable.length === 0) {
			return results;
		}
		return {
			valid: results.valid.filter(x => applicableTemplateIds.includes(x.templateID)),
			invalid: [...results.invalid, ...nonApplicable],
			totalCount: results.totalCount
		};
	}

	// RL-7759/RL-3988: Exclude inactive Catalogs from valid results (universal validation)
	private excludeInactiveCatalogs(results: IValidateInputListResult): IValidateInputListResult {
		if (this.childCharTypeId !== CharTypeId.Catalog) {
			return results;
		}
		const inactiveCatalogs = results.valid.filter(x => (x as unknown as { activeIndicator?: number })?.activeIndicator === 0);
		if (inactiveCatalogs.length === 0) {
			return results;
		}
		return {
			valid: results.valid.filter(x => (x as unknown as { activeIndicator?: number })?.activeIndicator !== 0),
			invalid: [...results.invalid, ...inactiveCatalogs],
			totalCount: results.totalCount
		};
	}

	ngOnDestroy(): void {
		this._subs.forEach(s => s.unsubscribe());
	}
}
